import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {
    isWritableDeclared,
    ProviderConnection,
} from '../types';
import {capabilitySnapshotCaption} from '../statusPresentation';

// FNP-1248 — WHAT THIS CREDENTIAL CAN DO.
//
// ⚠️ A READ-ONLY CREDENTIAL IS A WORKING CREDENTIAL. The single most important
// presentation decision in this component.
//
// The catalog says Route 53 canWriteRecords: true. A tenant who deliberately
// handed Flexi a READ-ONLY role has a connection that cannot write, and that is
// a correct, intentional, fully-functional setup — not a fault. Rendering those
// capabilities as red crosses, or as an "incomplete" state with a warning, tells
// a careful tenant that their careful choice is broken.
//
// So capabilities render as PRESENCE and ABSENCE ("can create zones" /
// "cannot create zones"), in neutral tones, with no remediation prompt. The only
// thing coloured red in this module is an actual failure.

const CapabilityRow = (
    {
        label,
        granted,
    }: {
        label: string,
        granted: boolean,
    },
) => (
    <Group gap={'xs'} justify={'space-between'} wrap={'nowrap'}>
        <Text size={'sm'}>{label}</Text>
        {/* ⚠️ 'gray' for absence, NOT 'red'. Absence is a statement about the
            credential the tenant chose to supply, not an error in it. */}
        <Badge
            variant={'light'}
            color={granted ? 'green' : 'gray'}
            size={'sm'}
        >
            {granted ? I18n.get('Yes') : I18n.get('No')}
        </Badge>
    </Group>
);

export const CapabilitySnapshot = (
    {connection}: { connection: ProviderConnection },
) => {

    const capabilities = connection.capabilities;

    // `capabilities` is non-null in the schema, but a partial/degraded response
    // must not throw inside a render.
    if (!capabilities) {
        return (
            <Text size={'sm'} c={'dimmed'}>
                {I18n.get('Flexi has no capability snapshot for this connection.')}
            </Text>
        );
    }

    const recordTypes = capabilities.supportedRecordTypes ?? [];

    // ⚠️ FNP-1233 — the DECLARED write capability, read through the fail-closed
    // helper. See types.ts: an undefined `writable` (which is what every
    // connection returns until the schema change deploys) resolves to NOT
    // writable, never to writable-by-omission.
    const writableDeclared = isWritableDeclared(connection);

    return (
        <Stack gap={'sm'}>

            {/* ⚠️ The caption changes tense on a non-ACTIVE connection. A
                snapshot records what the credential proved AT CONNECT TIME; next
                to a broken status, presenting it as current fact would claim the
                connection can do things while nothing works at all. */}
            <Text size={'xs'} c={'dimmed'} fw={500}>
                {capabilitySnapshotCaption(connection)}
            </Text>

            {/* ─── FNP-1233: THE TENANT'S DECLARATION ───
                Shown first because it is what makes the rest meaningful: with
                writes undeclared, the write rows below are uniformly No, and a
                reader with no explanation would think the credential failed. */}
            <Group gap={'xs'} justify={'space-between'} wrap={'nowrap'}>
                <Text size={'sm'}>{I18n.get('Declared to write')}</Text>
                <Badge
                    variant={'light'}
                    color={writableDeclared ? 'green' : 'gray'}
                    size={'sm'}
                    data-testid={'writable-declared'}
                >
                    {writableDeclared
                        ? I18n.get('This role can create and delete zones')
                        : I18n.get('Read-only')}
                </Badge>
            </Group>

            <CapabilityRow
                label={I18n.get('Write records')}
                granted={capabilities.canWriteRecords}
            />
            <CapabilityRow
                label={I18n.get('Create zones')}
                granted={capabilities.canCreateZones}
            />
            <CapabilityRow
                label={I18n.get('Delete zones')}
                granted={capabilities.canDeleteZones}
            />
            <CapabilityRow
                label={I18n.get('Set nameservers')}
                granted={capabilities.canSetNameservers}
            />

            <Stack gap={4}>
                <Text size={'sm'}>{I18n.get('Record types')}</Text>
                {recordTypes.length > 0 ? (
                    <Group gap={4}>
                        {recordTypes.map(recordType => (
                            <Badge
                                key={recordType}
                                variant={'outline'}
                                color={'gray'}
                                size={'sm'}
                            >
                                {recordType}
                            </Badge>
                        ))}
                    </Group>
                ) : (
                    // Distinct from "none supported": an empty list here means
                    // the snapshot did not enumerate them.
                    <Text size={'xs'} c={'dimmed'}>
                        {I18n.get('Not enumerated for this connection')}
                    </Text>
                )}
            </Stack>

        </Stack>
    );
};
